/**
 * Careers JobAdder listing — JA-004 sidebar filters + desktop table (Chakra multipart theme).
 * File: `careers-job-board.js`. Consumers: useMultiStyleConfig('CareersJobBoard', {}).
 */

import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const parts = [
    'boardContainer',
    'boardGrid',
    'boardTitleArea',
    'boardHeading',
    'boardSubheading',
    'filterStickyOuter',
    'filterActiveWrap',
    'filterChipOuter',
    'filterChipLabel',
    'filterChipRemove',
    'filterClearAll',
    'filterSectionWrap',
    'filterSectionHeading',
    'filterLocationOuter',
    'filterLocationsScroll',
    'filterCheckbox',
    'filterCheckboxLabelText',
    'filterMutedText',
    'filterSectionDivider',
    'filterCheckboxList',
    'filterMobileBar',
    'filterMobileTrigger',
    'filterFlyoutContent',
    'filterFlyoutHeader',
    'filterFlyoutTitle',
    'filterFlyoutAccordion',
    'filterFlyoutAccordionItem',
    'filterFlyoutAccordionButton',
    'filterFlyoutExpandGlyph',
    'filterFlyoutAccordionTitle',
    'filterFlyoutAccordionPanel',
    'filterFlyoutCheckboxStack',
    'listingSkeletonBanner',
    'listingSkeletonRow',
    'listingErrorOuter',
    'listingErrorText',
    'listingErrorRetryBtn',
    'listingEmptyOuter',
    'listingEmptyHeading',
    'listingEmptyText',
    'listingMobileOuter',
    'listingMobileRow',
    'listingMobileRouterLink',
    'listingMobileTitle',
    'listingMobileDetailStack',
    'listingMobileDtLabel',
    'listingMobileFooterLinkText',
    'listingTable',
    'listingTableDividerRow',
    'listingThLeading',
    'listingThInner',
    'listingThLocation',
    'listingThTrailing',
    'listingTdLeading',
    'listingTdCell',
    'listingTdLocation',
    'listingTdAction',
    'listingLinkTitleRow',
    'listingLinkViewDetails',
    'listingFooterRow',
    'listingResultSummaryOuter',
    'listingResultSummary',
    'listingPaginationWrap',
    'listingPaginationStack',
    'listingPaginationGhostIcon',
    'listingPaginationEllipsisText',
    'listingPaginationPageButton',
    'listingPaginationPageButtonSelected',
];

const baseStyle = {
    boardContainer: {
      color: 'gray.900',
      maxW: '1628px',
      // paddingX: {base: '0', md: '15px', lg: '15px'},
      pl: {base: '0', md: '15px', lg: '15px'},
      pr: {base: '0', md: '15px', lg: '15px'},
      pt: {base: '20px', md: '80px'},
      pb: 12,
    },
    boardGrid: {
      columnGap: {base: 6, md: '53px'},
      rowGap: {base: 8, md: 15},
      templateColumns: {base: '1fr', md: 'minmax(220px, 23.3096%) minmax(0, 1fr)'},
    },
    boardTitleArea: {
      scrollMarginTop: {base: 'calc(var(--header-height, 72px) + 24px)', md: 'calc(var(--header-height, 80px) + 32px)'},
      mb: {base: 6, md: 10},
      pb: {base: 2, md: 3},
    },
    boardHeading: {
      as: 'h2',
      color: '#333435',
      fontFamily: 'mono',
      fontSize: {base: '1.875rem', md: '42.13px'},
      fontWeight: 100,
      letterSpacing: 'normal',
      lineHeight: 1.12,
      mx: 'auto',
      mb: '28px',
      pb: 0,
      textAlign: {base: 'left', md: 'center'},
      width: 'full',
      maxW: '100%',
    },
    boardSubheading: {
      color: 'gray.600',
      fontSize: 'sm',
      fontWeight: 400,
      mt: {base: 4, md: 0},
      textAlign: {base: 'left', md: 'center'},
    },
    filterStickyOuter: {
      position: {base: 'static', md: 'sticky'},
      top: '100px',
    },
    filterActiveWrap: {
      borderBottomStyle: 'solid',
      borderBottomWidth: '1px',
      borderBottomColor: {base: '#c4c4c4', md: '#e5e5e5'},
      color: '#000',
      letterSpacing: '0.12em',
      mb: {base: 6, md: '30px'},
      mt: {base: 0, md: '30px'},
      pb: {base: 4, md: '35px'},
    },
    filterChipOuter: {
      borderColor: '#000',
      borderRadius: '2px',
      borderStyle: 'solid',
      borderWidth: '1px',
      display: 'inline-block',
      pb: '9px',
      pl: '16px',
      position: 'relative',
      pr: '44px',
      pt: '5px',
      maxH: '37px',
    },
    filterChipLabel: {
      color: '#000',
      display: 'inline-block',
      fontSize: '14px',
      fontWeight: 'normal',
      lineHeight: '18px',
    },
    filterChipRemove: {
      color: '#000',
      colorScheme: 'gray',
      h: 'auto',
      minW: 'auto',
      p: 0,
      position: 'absolute',
      right: '10px',
      size: 'xs',
      top: '50%',
      transform: 'translateY(-50%)',
      variant: 'ghost',
      w: 'auto',
      sx: {
        '& svg': {
          height: '13px',
          width: '13px',
          flexShrink: 0,
          color: 'inherit',
        },
      },
      _active: {bg: 'transparent'},
      _hover: {bg: 'transparent', color: '#000'},
    },
    filterClearAll: {
      bg: '#404040',
      borderRadius: '2px',
      color: '#fff',
      display: 'inline-block',
      fontSize: '14px',
      fontWeight: 'normal',
      lineHeight: '18px',
      mt: 4,
      pt: '9px',
      pr: '16px',
      pb: '10px',
      pl: '16px',
      w: 'auto',
      _active: {bg: '#404040'},
      _hover: {bg: '#4a4a4a', color: '#fff'},
    },
    filterSectionWrap: {
      mb: 8,
    },
    filterSectionHeading: {
      as: 'h3',
      color: '#000',
      display: 'block',
      fontFamily: 'body',
      fontSize: {base: '12px', lg: '14px'},
      fontStyle: 'normal',
      fontWeight: 700,
      h: '42px',
      letterSpacing: {base: '0.09em', md: '0.12em'},
      lineHeight: '42px',
      m: 0,
      mb: '13px',
      p: 0,
      textTransform: 'capitalize',
    },
    filterLocationOuter: {
      mb: 2,
    },
    filterLocationsScroll: {
      align: 'stretch',
      maxH: '375px',
      overflowY: 'auto',
      spacing: 3,
      pl: {base: 0, md: '35px'},
    },
    filterCheckboxList: {
      align: 'stretch',
      pl: {base: 0, md: '35px'},
      spacing: 3,
    },
    filterCheckbox: {
      sx: {
        '& .chakra-checkbox__control': {borderRadius: 0},
      },
    },
    filterCheckboxLabelText: {
      color: 'gray.900',
      fontSize: 'sm',
    },
    filterMutedText: {
      color: 'gray.400',
      fontSize: 'sm',
    },
    filterSectionDivider: {
      borderColor: 'gray.200',
      mb: '35px',
    },
    filterMobileBar: {
      mb: 6,
    },
    filterMobileTrigger: {
      bg: 'white',
      borderColor: '#c4c4c4',
      borderRadius: 0,
      borderWidth: '1px',
      color: '#000',
      fontSize: '12px',
      fontWeight: 600,
      h: '50px',
      letterSpacing: 'wider',
      minH: '50px',
      py: 0,
      textTransform: 'uppercase',
      variant: 'outline',
      w: 'full',
      _active: {bg: 'white'},
      _hover: {bg: 'gray.50'},
    },
    filterFlyoutContent: {
      bg: 'white',
      borderLeftColor: '#c4c4c4',
      borderLeftWidth: '1px',
      boxShadow: 'none',
      marginLeft: 'auto',
      maxW: '414px',
      w: 'min(414px, 100vw)',
      _dark: {
        boxShadow: 'none',
      },
      _focus: {
        boxShadow: 'none',
      },
      _focusVisible: {
        boxShadow: 'none',
      },
    },
    filterFlyoutHeader: {
      borderBottomWidth: 0,
      mb: '5px',
      p: '17px',
      position: 'relative',
    },
    filterFlyoutTitle: {
      color: '#000',
      fontSize: '24px',
      fontWeight: 400,
      letterSpacing: 'normal',
      textAlign: 'center',
      textDecoration: 'none',
      textTransform: 'uppercase',
      width: 'full',
    },
    filterFlyoutAccordion: {
      borderColor: 'transparent',
      borderWidth: 0,
      outline: '0',
      w: 'full',
    },
    filterFlyoutAccordionItem: {
      borderBottomWidth: 0,
      borderWidth: 0,
    },
    filterFlyoutAccordionButton: {
      _hover: {bg: 'transparent'},
      color: '#000',
      fontSize: '14px',
      fontWeight: 700,
      justifyContent: 'space-between',
      minH: '50px',
      px: '20px',
      py: '5px',
      textAlign: 'left',
    },
    filterFlyoutAccordionTitle: {
      as: 'span',
      flex: 1,
      fontSize: '14px',
      fontWeight: 700,
      pr: 2,
      textAlign: 'left',
      textDecoration: 'none',
    },
    filterFlyoutExpandGlyph: {
      color: '#000',
      flexShrink: 0,
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: '18px',
      textAlign: 'center',
      w: '24px',
    },
    filterFlyoutAccordionPanel: {
      pb: '15px',
      pt: '5px',
    },
    filterFlyoutCheckboxStack: {
      align: 'stretch',
      pl: '20px',
      pr: '20px',
      spacing: 3,
      sx: {
        '& .chakra-checkbox__label': {
          fontSize: '12px',
        },
      },
    },
    listingSkeletonBanner: {
      borderRadius: 0,
      endColor: 'gray.100',
      mb: 4,
      startColor: 'gray.50',
    },
    listingSkeletonRow: {
      borderRadius: 0,
      endColor: 'gray.100',
      height: '56px',
      mb: 2,
      startColor: 'gray.50',
    },
    listingErrorOuter: {
      px: {base: 2, md: 0},
      py: 14,
      textAlign: 'center',
    },
    listingErrorText: {
      color: 'secondary.newSaleRed',
      fontSize: 'md',
      fontWeight: 500,
      letterSpacing: '-0.01em',
      mb: 6,
    },
    listingErrorRetryBtn: {
      bg: 'gray.900',
      borderRadius: 0,
      color: 'white',
      fontWeight: 'normal',
      px: 10,
      py: 6,
    },
    listingEmptyOuter: {
      textAlign: 'center',
      py: 14,
    },
    listingEmptyHeading: {
      as: 'h3',
      color: 'gray.900',
      fontWeight: '600',
      mb: 3,
      size: 'md',
    },
    listingEmptyText: {
      color: 'gray.500',
      fontSize: 'sm',
    },
    listingMobileOuter: {
      align: 'stretch',
      spacing: 5,
    },
    listingMobileRow: {
      borderBottomColor: '#c4c4c4',
      borderBottomWidth: '1px',
      pb: 5,
    },
    listingMobileRouterLink: {
      display: 'block',
    },
    listingMobileTitle: {
      color: '#000',
      fontWeight: '600',
      mb: 2,
    },
    listingMobileDetailStack: {
      align: 'start',
      color: '#000',
      fontSize: 'sm',
      spacing: 1,
    },
    listingMobileDtLabel: {
      as: 'span',
      color: '#000',
      fontWeight: 600,
    },
    listingMobileFooterLinkText: {
      as: 'span',
      alignItems: 'flex-end',
      color: '#1d1d1d',
      display: 'inline-flex',
      flexDirection: 'column',
      fontSize: '14px',
      fontWeight: 700,
      letterSpacing: '0.1px',
      mt: 4,
      textDecoration: 'none',
      textTransform: 'capitalize',
      sx: {
        '&::after': {
          alignSelf: 'flex-end',
          background: '#3a3a3a',
          content: '""',
          display: 'block',
          height: '1px',
          mt: '2px',
          width: '100%',
        },
        _focusVisible: {
          outline: '2px solid',
          outlineColor: '#1d1d1d',
          outlineOffset: '2px',
        },
        _hover: {textDecoration: 'none'},
      },
    },
    listingTable: {
      sx: {
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        width: '100%',
        '& tbody td': {
          paddingBlock: '8px',
          paddingInline: 0,
        },
        '& thead th': {
          paddingBlock: '8px',
          paddingLeft: 0,
          paddingRight: '10.5px',
        },
        '& thead th:last-of-type': {
          paddingRight: 0,
        },
      },
    },
    listingTableDividerRow: {
      borderBottomColor: {base: '#c4c4c4', md: 'gray.200'},
      borderBottomWidth: '1px',
    },
    listingThLeading: {
      border: 0,
      color: '#000',
      fontFamily: 'body',
      fontSize: '14px',
      fontWeight: 'bold',
      letterSpacing: '-0.01em',
      textTransform: 'none',
      verticalAlign: 'middle',
      whiteSpace: 'normal',
      width: 'auto',
    },
    listingThInner: {
      border: 0,
      color: '#000',
      fontFamily: 'body',
      fontSize: '14px',
      fontWeight: 'bold',
      letterSpacing: '-0.01em',
      textTransform: 'none',
      verticalAlign: 'middle',
      whiteSpace: 'normal',
    },
    listingThLocation: {
      border: 0,
      color: '#000',
      fontFamily: 'body',
      fontSize: '14px',
      fontWeight: 'bold',
      letterSpacing: '-0.01em',
      textTransform: 'none',
      verticalAlign: 'middle',
    },
    listingThTrailing: {
      border: 0,
      color: '#000',
      fontSize: '14px',
      textAlign: 'right',
      verticalAlign: 'middle',
      width: '7.44%',
    },
    listingTdLeading: {
      border: 0,
      color: '#000',
      fontSize: '14px',
      verticalAlign: 'middle',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
    listingTdCell: {
      border: 0,
      color: '#000',
      fontSize: '14px',
      verticalAlign: 'middle',
    },
    listingTdLocation: {
      border: 0,
      color: '#000',
      fontSize: '14px',
      verticalAlign: 'middle',
    },
    listingTdAction: {
      border: 0,
      fontSize: '14px',
      textAlign: 'right',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
      width: '7.44%',
    },
    listingLinkTitleRow: {
      bg: 'transparent',
      border: 'none',
      color: '#000',
      fontSize: '14px',
      fontWeight: 400,
      textDecoration: 'none',
    },
    listingLinkViewDetails: {
      alignItems: 'flex-end',
      bg: 'transparent',
      border: 'none',
      color: '#1d1d1d',
      display: 'inline-flex',
      flexDirection: 'column',
      fontSize: '14px',
      fontWeight: 700,
      letterSpacing: '0.1px',
      maxW: '100%',
      textDecoration: 'none',
      textTransform: 'capitalize',
      verticalAlign: 'baseline',
      whiteSpace: 'nowrap',
      sx: {
        '&::after': {
          alignSelf: 'flex-end',
          background: '#3a3a3a',
          content: '""',
          display: 'block',
          height: '1px',
          mt: '2px',
          width: '100%',
        },
        _focusVisible: {
          outline: '2px solid',
          outlineColor: '#1d1d1d',
          outlineOffset: '2px',
        },
        _hover: {
          color: '#1d1d1d',
          textDecoration: 'none',
        },
      },
    },
    listingFooterRow: {
      alignItems: {base: 'flex-start', md: 'center'},
      direction: 'row',
      flexWrap: 'nowrap',
      gap: {base: 2, md: 4},
      justifyContent: 'space-between',
      minW: 0,
      mt: {base: 4, md: 8},
      w: 'full',
    },
    listingResultSummaryOuter: {
      alignSelf: {base: 'center', md: 'stretch'},
      flex: {base: '1 1 auto', md: '1 1 auto'},
      minW: 0,
      textAlign: {base: 'end', md: 'end'},
      w: {base: 'auto', md: 'auto'},
    },
    listingResultSummary: {
      color: 'gray.600',
      display: 'block',
      fontSize: {base: 'xs', md: 'sm'},
      fontWeight: 400,
      lineHeight: {base: 1.25, md: 'normal'},
      minW: 0,
      textAlign: 'inherit',
      w: 'auto',
    },
    listingPaginationWrap: {
      alignSelf: {base: 'flex-start', md: 'flex-start'},
      flexShrink: 0,
      w: {base: 'auto', md: 'auto'},
    },
    listingPaginationStack: {
      spacing: {base: 1, md: 2},
    },
    listingPaginationGhostIcon: {
      color: 'gray.800',
      lineHeight: {base: '15px', md: '15px'},
      minH: {base: '15px', md: '15px'},
      minW: {base: '15px', md: '15px'},
      height: {base: '15px', md: '15px'},
      variant: 'ghost',
      w: 'auto',
      px: {base: 1, md: 1},
      py: {base: 0, md: 0},
      _active: {bg: 'transparent'},
      _hover: {bg: 'transparent'},
    },
    listingPaginationEllipsisText: {
      color: 'gray.500',
      fontSize: {base: 'xs', md: 'sm'},
      px: {base: 1, md: 2},
    },
    listingPaginationPageButton: {
      variant: 'ghost',
      bg: 'transparent',
      borderRadius: 0,
      color: 'gray.900',
      fontSize: {base: 'xs', md: 'sm'},
      fontWeight: 400,
      h: 'auto',
      lineHeight: {base: '15px', md: '15px'},
      minH: {base: '15px', md: '15px'},
      minW: {base: '15px', md: '15px'},
      height: {base: '15px', md: '15px'},
      px: {base: 0.5, md: 1},
      py: 0,
      w: 'auto',
      _active: {bg: 'transparent'},
      _hover: {bg: 'transparent'},
    },
    listingPaginationPageButtonSelected: {
      variant: 'ghost',
      bg: 'transparent',
      borderRadius: 0,
      color: 'gray.900',
      fontSize: {base: 'xs', md: 'sm'},
      fontWeight: 700,
      h: 'auto',
      lineHeight: {base: '15px', md: '15px'},
      minH: {base: '15px', md: '15px'},
      minW: {base: '15px', md: '15px'},
      height: {base: '15px', md: '15px'},
      px: {base: 0.5, md: 1},
      py: 0,
      w: 'auto',
      _active: {bg: 'transparent'},
      _hover: {bg: 'transparent'},
    },
};

const {defineMultiStyleConfig} = createMultiStyleConfigHelpers(parts);
export default defineMultiStyleConfig({baseStyle});
